/**
 * LA SALUD, EN LA PORTADA.  (US3, T047, FR-029)
 *
 * El propietario lo pidió así: *"yo pienso en estadísticas, métricas… salud…
 * salud de los nodos"*. La salud no es una pestaña aparte a la que hay que
 * acordarse de ir: si hay que ir a mirarla, se mira el día después.
 *
 * ── LA REGLA DE DISEÑO DE ESTE COMPONENTE ───────────────────────────────────
 *
 * **Cuando todo va bien, ocupa cuatro líneas. Cuando algo va mal, crece.**
 *
 * Lo contrario —una rejilla fija de 27 tarjetas verdes— entrena al ojo a no
 * mirar, y el día que una se pone roja está tan escondida entre las verdes como
 * si no estuviera. Aquí lo sano se resume en un número y solo lo que falla
 * ocupa sitio propio.
 */
import { useQuery } from '@tanstack/react-query'

import { api } from '../lib/api'

interface Metricas {
    cpuPct: number | null
    /** Sobre cuánto tiempo está promediada la CPU. Ver el backend. */
    cpuVentanaSeg: number | null
    nucleos: number
    carga1: number
    memoria: { totalBytes: number; disponibleBytes: number; usoPct: number }
    disco: { totalBytes: number; disponibleBytes: number; usoPct: number } | null
    uptimeSegundos: number
}

interface Contenedor {
    nombre: string
    estado: string
    salud: 'healthy' | 'unhealthy' | 'starting' | null
    /** Código de salida de los `exited`. Separa "lo paramos" de "se cayó". */
    salida: number | null
}

export interface Salud {
    panel: 'up'
    servidor: { estado: 'up' | 'degraded'; detalle: string; metricas: Metricas }
    contenedores: {
        total: number | null
        sanos: number | null
        /** Parados. Se cuentan, pero NO son alarma (ver el backend). */
        parados: number | null
        /** Corriendo y fallando AHORA. Estos sí. */
        problemas: number | null
        lista: Contenedor[]
        detalle: string
    }
    chatwoot: { estado: 'up' | 'degraded' | 'down'; version: string | null; detalle: string }
    bot: { estado: 'healthy' | 'degraded' | 'down'; detalle: string }
    agendas: { accountId: number; name: string; estado: 'up' | 'down' | 'sin-configurar'; detalle: string }[]
    medidoEn: string
}

const COLOR: Record<string, string> = {
    bien: 'bg-emerald-500',
    regular: 'bg-amber-500',
    mal: 'bg-rose-500',
    mudo: 'bg-neutral-600',
}

function Punto({ tono }: { tono: keyof typeof COLOR }) {
    return <span className={`inline-block size-2 shrink-0 rounded-full ${COLOR[tono]}`} />
}

function gb(bytes: number): string {
    return `${(bytes / 1e9).toFixed(1)} GB`
}

/**
 * Sobre qué periodo está promediada la CPU.
 *
 * Sin esto, un "12%" puede ser los últimos 30 s o las últimas seis horas, y no
 * hay forma de distinguirlos. Un pico de diez minutos diluido en seis horas es
 * invisible; decir la ventana convierte el número en interpretable.
 */
function ventana(seg: number | null): string {
    if (seg === null) return ''
    if (seg < 90) return `media de ${seg} s`
    const min = Math.round(seg / 60)
    return min < 90 ? `media de ${min} min` : `media de ${Math.round(min / 60)} h`
}

function duracion(segundos: number): string {
    const d = Math.floor(segundos / 86_400)
    if (d >= 1) return `${d} día${d === 1 ? '' : 's'}`
    return `${Math.floor(segundos / 3600)} h`
}

/** Una barra que se pone ámbar/roja sola. El número solo no dice si es mucho. */
function Barra({ pct, etiqueta, detalle }: { pct: number; etiqueta: string; detalle: string }) {
    const tono = pct >= 90 ? 'mal' : pct >= 75 ? 'regular' : 'bien'
    return (
        <div className="min-w-0">
            <div className="flex items-baseline justify-between gap-2">
                <span className="text-[11px] text-neutral-500">{etiqueta}</span>
                <span className="text-[11px] tabular-nums text-neutral-400">{pct}%</span>
            </div>
            <div className="mt-1 h-1 overflow-hidden rounded-full bg-neutral-800">
                <div className={`h-full ${COLOR[tono]}`} style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
            <p className="mt-1 truncate text-[10px] text-neutral-600">{detalle}</p>
        </div>
    )
}

function Fila({ tono, nombre, detalle }: { tono: keyof typeof COLOR; nombre: string; detalle: string }) {
    return (
        <div className="flex items-center gap-2 text-xs">
            <Punto tono={tono} />
            <span className="text-neutral-300">{nombre}</span>
            <span className="ml-auto truncate pl-2 text-[11px] text-neutral-600">{detalle}</span>
        </div>
    )
}

const TONO_BOT = { healthy: 'bien', degraded: 'regular', down: 'mal' } as const
const TONO_CW = { up: 'bien', degraded: 'regular', down: 'mal' } as const
const TONO_AGENDA = { up: 'bien', down: 'mal', 'sin-configurar': 'regular' } as const

export function SaludInfra() {
    const { data, isLoading, error } = useQuery<Salud>({
        queryKey: ['salud-infra'],
        queryFn: () => api.get<Salud>('/overview/health'),
        // Sin `refetchInterval` (FR-012, y hay una regla de ESLint que lo impide):
        // sondear desde el navegador es el patrón que hundió a P05.
        staleTime: 20_000,
    })

    if (isLoading) {
        return <p className="text-xs text-neutral-600">Comprobando la salud…</p>
    }
    if (error || !data) {
        // ⚠️ NO se dibuja verde ni se calla. "No lo sé" es un estado, y se dice.
        return (
            <div className="rounded-lg border border-neutral-800 bg-neutral-900/40 px-4 py-3 text-xs text-amber-500">
                ⚠️ No se pudo comprobar la salud de la infraestructura.
            </div>
        )
    }

    const { servidor, contenedores, chatwoot, bot, agendas } = data
    const agendasMal = agendas.filter((a) => a.estado !== 'up')
    /*
     * ⚠️ "NO ESTÁ EN MARCHA" NO ES "ESTÁ ROTO".
     *
     * En esta máquina hay 8 contenedores parados y NINGUNO está averiado: el
     * dashboard viejo P05 (lo paramos nosotros), n8n de hace dos meses, y un
     * contenedor de init cuyo oficio es terminar. Pintarlos en rojo sería poner
     * ocho alarmas permanentes en la portada — y a la semana se dejan de mirar.
     *
     * Alarma solo lo que está corriendo y fallando AHORA.
     */
    const contenedoresMal = contenedores.lista.filter(
        (c) => (c.estado === 'running' && c.salud === 'unhealthy') || c.estado === 'restarting' || c.estado === 'dead',
    )
    const m = servidor.metricas

    return (
        <section className="space-y-3 rounded-lg border border-neutral-800 bg-neutral-900/40 p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">Salud</h3>
                <span className="text-[10px] text-neutral-600">
                    servidor en pie desde hace {duracion(m.uptimeSegundos)}
                </span>
            </div>

            {/* ── El servidor: tres barras, no tres números sueltos ─────────── */}
            <div className="grid gap-4 md:grid-cols-3">
                <Barra
                    pct={m.cpuPct ?? 0}
                    etiqueta="CPU"
                    detalle={
                        m.cpuPct === null
                            ? 'midiendo…' // primera lectura: aún no hay derivada
                            : `${m.nucleos} núcleos · carga ${m.carga1} · ${ventana(m.cpuVentanaSeg)}`
                    }
                />
                <Barra
                    pct={m.memoria.usoPct}
                    etiqueta="Memoria"
                    detalle={`${gb(m.memoria.disponibleBytes)} libres de ${gb(m.memoria.totalBytes)}`}
                />
                {m.disco ? (
                    <Barra
                        pct={m.disco.usoPct}
                        etiqueta="Disco"
                        detalle={`${gb(m.disco.disponibleBytes)} libres de ${gb(m.disco.totalBytes)}`}
                    />
                ) : (
                    <div className="text-[11px] text-amber-500">Disco: sin lectura</div>
                )}
            </div>

            {/* ── Los servicios ─────────────────────────────────────────────── */}
            <div className="grid gap-x-6 gap-y-1.5 border-t border-neutral-900 pt-3 sm:grid-cols-2">
                <Fila tono={TONO_BOT[bot.estado]} nombre="Bot (Agendify)" detalle={bot.detalle} />
                <Fila
                    tono={TONO_CW[chatwoot.estado]}
                    nombre={`Chatwoot${chatwoot.version ? ` ${chatwoot.version}` : ''}`}
                    detalle={chatwoot.detalle}
                />
                <Fila
                    tono={contenedores.total === null ? 'mudo' : contenedoresMal.length > 0 ? 'mal' : 'bien'}
                    nombre="Contenedores"
                    detalle={
                        contenedores.total === null
                            ? contenedores.detalle // "no pudimos preguntar" ≠ "ninguno caído"
                            : `${contenedores.sanos} en marcha${contenedores.parados ? ` · ${contenedores.parados} parados` : ''}`
                    }
                />
                <Fila
                    tono={agendasMal.length > 0 ? 'mal' : 'bien'}
                    nombre="Agendas"
                    detalle={
                        agendas.length === 0
                            ? 'ninguna configurada'
                            : `${agendas.length - agendasMal.length}/${agendas.length} alcanzables`
                    }
                />
            </div>

            {/*
              ── SOLO LO QUE FALLA OCUPA SITIO ──────────────────────────────────
              Las agendas NO se colapsan en un semáforo: EasyAppointments no es
              multi-tenant, hay una instancia por negocio. Un verde global
              escondería una clínica que lleva horas sin poder dar citas.
            */}
            {(contenedoresMal.length > 0 || agendasMal.length > 0) && (
                <div className="space-y-1.5 border-t border-neutral-900 pt-3">
                    {contenedoresMal.map((c) => (
                        <Fila
                            key={c.nombre}
                            tono="mal"
                            nombre={c.nombre}
                            detalle={c.salud === 'unhealthy' ? 'en marcha pero fallando su chequeo' : c.estado}
                        />
                    ))}
                    {agendasMal.map((a) => (
                        <Fila
                            key={a.accountId}
                            tono={TONO_AGENDA[a.estado]}
                            nombre={`Agenda · ${a.name}`}
                            detalle={a.detalle}
                        />
                    ))}
                </div>
            )}
        </section>
    )
}
